import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { Logo } from "../../components/common/Logo";
import { motion } from "framer-motion";
import { apiClient } from "../../lib/api/client";
import { LoginModal } from "../auth/components/LoginModal";
import { RegisterModal } from "../auth/components/RegisterModal";

type FlowState =
    | "loading"           // Initial state, verifying store
    | "needs_login"       // User exists but not logged in
    | "needs_register"    // User doesn't exist, needs to create account
    | "ready_to_connect"  // User is authenticated, ready to connect
    | "connecting"        // Connection in progress (handshake initiation)
    | "success"           // Connection successful (unlikely here as WC redirects)
    | "error";            // Error state

interface VerifyResponse {
    success: boolean;
    store_url: string;
    store_name: string;
    alreadyConnected?: boolean;
    connectedToUser?: { email: string; name: string } | null;
    error?: string;
}

export default function ConnectWooCommercePage() {
    const [searchParams] = useSearchParams();
    const storeUrl = searchParams.get("store_url");
    const { isAuthenticated, user, isLoading: authLoading } = useAuthStore();
    const navigate = useNavigate();

    const [flowState, setFlowState] = useState<FlowState>("loading");
    const [storeName, setStoreName] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<string | null>(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [alreadyConnected, setAlreadyConnected] = useState(false);
    const [storeEmail, setStoreEmail] = useState<string | null>(null);

    // Verify the store and determine flow
    const verifyStore = useCallback(async () => {
        if (!storeUrl) {
            setFlowState("error");
            setError("Missing store URL. Please start the connection from your WordPress Admin.");
            setErrorCode("MISSING_PARAMS");
            return;
        }

        try {
            const response = await apiClient.get<VerifyResponse>(
                `/connect/woocommerce/verify?store_url=${encodeURIComponent(storeUrl)}`
            );

            const data = response.data;

            if (data.success) {
                setStoreName(data.store_name || storeUrl.replace(/^https?:\/\//, '').split('/')[0]);
                setAlreadyConnected(!!data.alreadyConnected);

                const freshIsAuthenticated = useAuthStore.getState().isAuthenticated;
                const freshUser = useAuthStore.getState().user;

                if (freshIsAuthenticated && freshUser) {
                    setFlowState("ready_to_connect");
                    return;
                }

                // Try to determine if user exists based on email from store
                if (data.connectedToUser?.email) {
                    setStoreEmail(data.connectedToUser.email);
                }

                // Check if user with this email exists
                const emailToCheck = data.connectedToUser?.email;
                if (emailToCheck) {
                    try {
                        const userCheckResponse = await apiClient.get(
                            `/connect/check-user?email=${encodeURIComponent(emailToCheck)}`
                        );

                        if (userCheckResponse.data.success && userCheckResponse.data.exists) {
                            setFlowState("needs_login");
                            setShowLoginModal(true);
                        } else {
                            setFlowState("needs_register");
                            setShowRegisterModal(true);
                        }
                    } catch {
                        setFlowState("needs_register");
                        setShowRegisterModal(true);
                    }
                } else {
                    // No email context - default to login if there's no way to know
                    setFlowState("needs_login");
                    setShowLoginModal(true);
                }
            } else {
                setFlowState("error");
                setError(data.error || "Store verification failed");
                setErrorCode("VERIFICATION_FAILED");
            }
        } catch (err: unknown) {
            const error = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
            const errorMessage = error.response?.data?.error || error.message || "Failed to verify store. Please check the URL and try again.";
            const code = error.response?.data?.code || "VERIFICATION_FAILED";
            setError(errorMessage);
            setErrorCode(code);
            setFlowState("error");
        }
    }, [storeUrl]);

    useEffect(() => {
        // Wait for authStore rehydration to avoid flickering login modal
        if (!authLoading && flowState === "loading") {
            const hasAuthData = !!localStorage.getItem('auth-storage');
            const timer = setTimeout(() => {
                verifyStore();
            }, hasAuthData && !isAuthenticated ? 300 : 0);
            return () => clearTimeout(timer);
        }
    }, [authLoading, flowState, verifyStore, isAuthenticated]);

    // Handle authentication success
    useEffect(() => {
        if (isAuthenticated && user && (flowState === "needs_login" || flowState === "needs_register")) {
            setShowLoginModal(false);
            setShowRegisterModal(false);

            // Check if email verification is required
            const skipVerification = (window as any).__DASHBAL_CONFIG__?.skipEmailVerification;
            if (!user.emailVerified && !skipVerification) {
                const returnPath = `/connect/woocommerce/start?${searchParams.toString()}`;
                navigate(`/check-email?redirect=${encodeURIComponent(returnPath)}`);
            } else {
                setFlowState("ready_to_connect");
            }
        }
    }, [isAuthenticated, user, flowState, searchParams, navigate]);

    const handleConnect = async () => {
        if (!storeUrl) return;

        setFlowState("connecting");
        setError(null);
        setErrorCode(null);

        try {
            const response = await apiClient.post("/connect/woocommerce/initiate", {
                store_url: storeUrl
            });

            if (response.data.success && response.data.auth_url) {
                // Redirect user to WooCommerce Approval page
                window.location.href = response.data.auth_url;
            } else {
                throw new Error(response.data.error || "Failed to initiate connection");
            }
        } catch (err: unknown) {
            const error = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
            const errorMessage = error.response?.data?.error || error.message || "An unexpected error occurred.";
            const code = error.response?.data?.code || "CONNECTION_FAILED";
            setError(errorMessage);
            setErrorCode(code);
            setFlowState("error");
        }
    };

    const handleModalClose = () => {
        setShowLoginModal(false);
        setShowRegisterModal(false);
    };

    const switchToLogin = () => {
        setShowRegisterModal(false);
        setShowLoginModal(true);
        setFlowState("needs_login");
    };

    const switchToRegister = () => {
        setShowLoginModal(false);
        setShowRegisterModal(true);
        setFlowState("needs_register");
    };

    const handleRetry = () => {
        setError(null);
        setErrorCode(null);
        setFlowState("loading");
        verifyStore();
    };

    if (authLoading || flowState === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-neutral-50">
                <div className="text-center">
                    <div className="spinner w-10 h-10 text-accent-primary mx-auto mb-4" />
                    <p className="text-neutral-600">Preparing connection...</p>
                </div>
            </div>
        );
    }

    // Missing store URL
    if (!storeUrl) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 p-4">
                <div className="bg-white p-8 rounded-xl shadow-sm border border-neutral-200 max-w-md w-full text-center">
                    <div className="mx-auto w-12 h-12 bg-error-50 rounded-full flex items-center justify-center mb-4">
                        <span className="w-6 h-6 text-error-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_3477644f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3477644f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                    <h2 className="text-xl font-semibold text-neutral-900 mb-2">Missing Information</h2>
                    <p className="text-neutral-600 mb-6">
                        No store URL specified. Please start the connection from your WordPress Admin.
                    </p>
                    <Link to="/" className="btn-secondary w-full justify-center">
                        Go Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50 flex flex-col font-sans">
            {/* Login Modal */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={handleModalClose}
                onSwitchToRegister={switchToRegister}
            />

            {/* Register Modal - with pre-filled data */}
            <RegisterModal
                isOpen={showRegisterModal}
                onClose={handleModalClose}
                onSwitchToLogin={switchToLogin}
                prefillEmail={storeEmail || undefined}
            />

            {/* Header */}
            <header className="bg-white border-b border-neutral-200 py-4 px-6">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Logo className="w-8 h-8" />
                        <span className="font-display font-medium text-xl text-text-primary">Dashbal</span>
                    </Link>

                    {/* Show login/register toggle when modals are open */}
                    {(flowState === "needs_login" || flowState === "needs_register") && (
                        <div className="flex items-center gap-4">
                            {flowState === "needs_register" && (
                                <button onClick={switchToLogin} className="text-sm text-accent-primary hover:underline">
                                    Already have an account? Log in
                                </button>
                            )}
                            {flowState === "needs_login" && (
                                <button onClick={switchToRegister} className="text-sm text-accent-primary hover:underline">
                                    Need an account? Sign up
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex flex-col items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full max-w-md space-y-6 md:space-y-8"
                >
                    <div className="bg-white p-5 md:p-8 rounded-2xl shadow-sm border border-neutral-200">
                        <div className="text-center mb-8">
                            <div className="flex items-center justify-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-neutral-100 rounded-xl flex items-center justify-center">
                                    <Logo className="w-10 h-10" />
                                </div>
                                <div className="w-8 h-px bg-neutral-300 border-t border-dashed border-neutral-400" />
                                <div className="w-16 h-16 bg-[#96588A]/10 rounded-xl flex items-center justify-center">
                                    <img src={`${((window as any).__DASHBAL_CONFIG__?.cdnUrl || 'https://cdn.dashbal.com').replace(/\/$/, '')}/logos/channel_logos/woocommerce/Woo_primary.png`} alt="WooCommerce" className="w-10 h-10 object-contain" />
                                </div>
                            </div>

                            {flowState === "ready_to_connect" && (
                                <>
                                    <h1 className="text-2xl font-bold text-neutral-900 mb-2">Confirm Connection</h1>
                                    <p className="text-neutral-600">
                                        Connect <strong>{storeName}</strong> to your Dashbal account <strong>{user?.email}</strong>
                                    </p>
                                    {alreadyConnected && (
                                        <div className="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                                            <p className="text-sm text-warning-700">
                                                This store is already connected to another Dashbal account. Connecting again will update the link.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {flowState === "connecting" && (
                                <>
                                    <h1 className="text-2xl font-bold text-neutral-900 mb-2">Connecting...</h1>
                                    <p className="text-neutral-600">
                                        Please wait while we redirect you to your store.
                                    </p>
                                </>
                            )}

                            {(flowState === "needs_login" || flowState === "needs_register") && (
                                <>
                                    <h1 className="text-2xl font-bold text-neutral-900 mb-2">Connect Your Store</h1>
                                    <p className="text-neutral-600">
                                        {flowState === "needs_login"
                                            ? "Log in to your Dashbal account to complete the connection."
                                            : "Create a Dashbal account to connect your WooCommerce store."}
                                    </p>
                                </>
                            )}

                            {flowState === "error" && (
                                <>
                                    <h1 className="text-2xl font-bold text-neutral-900 mb-2">Connection Error</h1>
                                    <p className="text-neutral-600">
                                        There was a problem connecting your store.
                                    </p>
                                </>
                            )}
                        </div>

                        {error && (
                            <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-start gap-3">
                                <span className="w-5 h-5 text-error-500 mt-0.5 flex-shrink-0" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ff684cae.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ff684cae.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                <div>
                                    <p className="text-sm text-error-700">{error}</p>
                                    {errorCode === "ALREADY_CONNECTED_OTHER_USER" && (
                                        <p className="text-xs text-error-600 mt-1">
                                            Contact support if you need to transfer this store.
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            {flowState === "ready_to_connect" && (
                                <>
                                    <button
                                        onClick={handleConnect}
                                        className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                                    >
                                        <span>Confirm & Connect</span>
                                        <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a2b3c2e5.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a2b3c2e5.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </button>
                                    <button
                                        onClick={() => navigate("/boards")}
                                        className="btn-secondary w-full py-3 justify-center"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}

                            {flowState === "connecting" && (
                                <div className="flex items-center justify-center py-4">
                                    <div className="spinner w-8 h-8 text-accent-primary" />
                                </div>
                            )}

                            {(flowState === "needs_login" || flowState === "needs_register") && (
                                <>
                                    <button
                                        onClick={() => flowState === "needs_login" ? setShowLoginModal(true) : setShowRegisterModal(true)}
                                        className="btn-primary w-full py-3 flex items-center justify-center gap-2"
                                    >
                                        <span>{flowState === "needs_login" ? "Log In" : "Create Account"}</span>
                                    </button>
                                    <button
                                        onClick={() => navigate("/")}
                                        className="btn-secondary w-full py-3 justify-center block text-center"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}

                            {flowState === "error" && (
                                <>
                                    <button
                                        onClick={handleRetry}
                                        className="btn-primary w-full py-3"
                                    >
                                        Try Again
                                    </button>
                                    <Link to="/" className="btn-secondary w-full py-3 justify-center block text-center">
                                        Go Home
                                    </Link>
                                </>
                            )}
                        </div>

                        <div className="mt-6 text-center">
                            <p className="text-xs text-neutral-500">
                                By connecting, you grant Dashbal permission to access your store's data as defined by the application scopes.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </main>
        </div>
    );
}
